/**
 * Catalogue des canaux de données ouverts aux agents.
 *
 * `signal-catalog.ts` répond à « quel signal chercher ? » ; ce fichier répond
 * à « où le trouver, concrètement ? ». Chaque canal nomme des plateformes
 * réelles (réseau professionnel, congrès et salons, appels d'offres,
 * registres, avis, communautés…), les familles de signaux qu'il alimente et
 * des gabarits de requêtes prêts à l'emploi.
 *
 * Module neutre : aucune dépendance serveur, importable depuis un composant
 * client comme depuis le prompt de l'agent (voir `lib/source-connectors.ts`
 * pour l'exécution et `lib/agent-tools.ts` pour l'exposition à l'agent).
 */

export type SourceGroupId =
  | "professional-network"
  | "events"
  | "hiring"
  | "procurement"
  | "registries"
  | "press"
  | "regulation"
  | "voice"
  | "technical"
  | "talks";

export type SourceChannel = {
  id: string;
  label: string;
  group: SourceGroupId;
  /** Ce que le canal apporte concrètement à un agent de prospection. */
  yields: string;
  /** Identifiants de familles de `signal-catalog.ts` que le canal alimente. */
  families: readonly string[];
  /** Domaines à cibler avec l'opérateur `site:` (vide = web ouvert). */
  domains: readonly string[];
  /**
   * Gabarits de requêtes. Variables disponibles :
   * `{org}` organisation visée, `{topic}` marché/métier, `{role}` fonction,
   * `{domain}` domaine du site de l'organisation, `{year}` année en cours.
   * Un gabarit contenant une variable non fournie est ignoré.
   */
  patterns: readonly string[];
  /** Zone couverte, pour ne pas imposer des sources françaises hors de France. */
  scope: "fr" | "eu" | "global";
  /**
   * `true` quand les pages du canal sont lisibles publiquement (lecture via
   * `readSource`). `false` = résultats de recherche uniquement : la page
   * complète est derrière un compte, et l'agent ne contourne jamais un accès.
   */
  readable: boolean;
};

export const SOURCE_GROUP_LABELS: Record<SourceGroupId, string> = {
  "professional-network": "Réseau professionnel",
  events: "Congrès, salons et événements",
  hiring: "Emploi et capacité",
  procurement: "Achats et appels d’offres",
  registries: "Registres et données d’entreprise",
  press: "Presse, communiqués et financement",
  regulation: "Réglementation et conformité",
  voice: "Voix du client et communautés",
  technical: "Plateformes techniques et statut",
  talks: "Prises de parole et contenus longs",
};

export const sourceChannels: readonly SourceChannel[] = [
  // — Réseau professionnel ————————————————————————————————————————
  {
    id: "linkedin-company",
    label: "Pages entreprise LinkedIn",
    group: "professional-network",
    yields:
      "Actualités officielles, annonces de croissance, ouvertures de sites, partenariats, effectifs et rythme de publication d’une organisation.",
    families: ["professional-network", "growth", "organization-change", "product-change", "go-to-market"],
    domains: ["linkedin.com/company"],
    patterns: [
      'site:linkedin.com/company "{org}"',
      'site:linkedin.com "{org}" (annonce OR lancement OR partenariat OR ouverture OR "nous sommes fiers")',
      'site:linkedin.com/company {topic} {year}',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "linkedin-posts",
    label: "Publications publiques LinkedIn",
    group: "professional-network",
    yields:
      "Le signal le plus daté et le plus explicite du réseau professionnel : dirigeants et opérationnels qui décrivent un problème, cherchent un prestataire, annoncent un chantier ou racontent un retour d’expérience.",
    families: ["professional-network", "direct-demand", "manual-operations", "operational-friction", "vendor-change"],
    domains: ["linkedin.com/posts", "linkedin.com/pulse"],
    patterns: [
      'site:linkedin.com/posts "{org}"',
      'site:linkedin.com/posts {topic} ("nous recherchons" OR "quelqu\'un aurait" OR "recommandation" OR "appel à" OR "retour d\'expérience")',
      'site:linkedin.com/posts {topic} ("nous lançons" OR "nous déployons" OR "migration" OR "nouveau projet") {year}',
      'site:linkedin.com/pulse {topic} ({role} OR direction)',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "linkedin-people",
    label: "Profils et prises de poste LinkedIn",
    group: "professional-network",
    yields:
      "Identification nominative de l’interlocuteur, arrivée d’un nouveau responsable (fenêtre de décision de 90 jours), création d’une fonction, périmètre exact d’un poste.",
    families: ["professional-network", "organization-change", "reachability", "workforce"],
    domains: ["linkedin.com/in"],
    patterns: [
      'site:linkedin.com/in "{org}" {role}',
      'site:linkedin.com/in "{org}" (directeur OR responsable OR head OR manager)',
      'site:linkedin.com "{org}" ("a rejoint" OR "nouveau poste" OR "ravi de rejoindre" OR "prend la direction") {year}',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "linkedin-jobs",
    label: "Offres d’emploi LinkedIn",
    group: "professional-network",
    yields:
      "Postes publiés avec mission détaillée, outils cités, taille d’équipe et motif du recrutement — souvent la description littérale d’un processus manuel.",
    families: ["professional-network", "workforce", "technology", "manual-operations"],
    domains: ["linkedin.com/jobs"],
    patterns: [
      'site:linkedin.com/jobs "{org}"',
      'site:linkedin.com/jobs {topic} {role}',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "linkedin-events",
    label: "Événements et webinaires LinkedIn",
    group: "professional-network",
    yields:
      "Webinaires, tables rondes et lancements organisés ou relayés par l’organisation, avec les intervenants nommés — une voie de contact naturelle.",
    families: ["professional-network", "events", "go-to-market", "reachability"],
    domains: ["linkedin.com/events"],
    patterns: [
      'site:linkedin.com/events {topic} {year}',
      'site:linkedin.com/events "{org}"',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "public-social",
    label: "Autres réseaux publics (X, Bluesky, Mastodon)",
    group: "professional-network",
    yields: "Réactions à chaud, incidents, mécontentement envers un fournisseur, annonces relayées hors LinkedIn.",
    families: ["professional-network", "vendor-change", "operational-friction", "security", "customer-voice"],
    domains: ["x.com", "bsky.app", "mastodon.social"],
    patterns: [
      'site:x.com "{org}" ({topic} OR panne OR incident OR migration)',
      '"{org}" (site:x.com OR site:bsky.app) {year}',
    ],
    scope: "global",
    readable: false,
  },

  // — Congrès, salons et événements ————————————————————————————————
  {
    id: "trade-fairs",
    label: "Salons, congrès et conventions",
    group: "events",
    yields:
      "Le calendrier d’un marché : qui expose, qui parraine, qui prend la parole et quand. Un exposant a un budget, une nouveauté à présenter et une équipe joignable à date connue.",
    families: ["events", "go-to-market", "budget", "product-change"],
    domains: [
      "eventseye.com",
      "10times.com",
      "tradefairdates.com",
      "salons-online.com",
      "viparis.com",
      "expo-events.com",
    ],
    patterns: [
      '(site:eventseye.com OR site:10times.com OR site:tradefairdates.com) {topic} {year}',
      '(salon OR congrès OR convention OR "trade fair") {topic} {year} (exposants OR "liste des exposants" OR programme)',
      '"{org}" (exposant OR stand OR sponsor OR partenaire) (salon OR congrès OR convention) {year}',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "conference-programs",
    label: "Programmes de congrès et intervenants",
    group: "events",
    yields:
      "Les prises de parole nommées : un responsable qui présente un projet en congrès raconte publiquement son chantier, ses contraintes et son calendrier.",
    families: ["events", "reachability", "organization-change", "manual-operations"],
    domains: [],
    patterns: [
      '"{org}" (intervenant OR conférencier OR keynote OR "table ronde" OR "retour d\'expérience") (congrès OR colloque OR journées OR summit) {year}',
      '{topic} (programme OR "call for papers" OR "appel à communications") (congrès OR colloque OR symposium) {year}',
      '"{org}" {role} (présentera OR interviendra OR animera) {year}',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "webinars-meetups",
    label: "Webinaires, meetups et billetteries",
    group: "events",
    yields:
      "Événements de plus petite maille, souvent gratuits et très datés, avec l’organisateur et les intervenants en clair.",
    families: ["events", "reachability", "go-to-market"],
    domains: ["eventbrite.fr", "eventbrite.com", "lu.ma", "meetup.com", "billetweb.fr", "weezevent.com"],
    patterns: [
      '(site:eventbrite.fr OR site:lu.ma OR site:meetup.com OR site:billetweb.fr) {topic} {year}',
      '"{org}" (webinaire OR webinar OR "petit-déjeuner" OR atelier) {year}',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "sector-associations",
    label: "Fédérations, syndicats et annuaires métier",
    group: "events",
    yields:
      "La liste des organisations d’un marché (adhérents, membres, lauréats) et leur agenda commun : la meilleure porte d’entrée pour constituer un univers de comptes.",
    families: ["events", "compliance", "growth"],
    domains: [],
    patterns: [
      '(fédération OR syndicat OR union OR association) {topic} (adhérents OR membres OR annuaire)',
      '{topic} (annuaire OR "liste des membres" OR palmarès OR lauréats) {year}',
    ],
    scope: "global",
    readable: true,
  },

  // — Emploi et capacité ——————————————————————————————————————————
  {
    id: "job-boards",
    label: "Sites d’emploi et pages carrière",
    group: "hiring",
    yields:
      "Mission, outils, organisation de l’équipe, séniorité et urgence. Un poste republié ou dupliqué signale une douleur qui dure.",
    families: ["workforce", "technology", "manual-operations", "growth", "cost-pressure"],
    domains: [
      "welcometothejungle.com",
      "apec.fr",
      "francetravail.fr",
      "hellowork.com",
      "indeed.fr",
      "glassdoor.fr",
    ],
    patterns: [
      '(site:welcometothejungle.com OR site:apec.fr OR site:hellowork.com) "{org}"',
      '(site:welcometothejungle.com OR site:apec.fr OR site:francetravail.fr) {topic} {role}',
      'site:{domain} (carrière OR carrieres OR recrutement OR jobs)',
    ],
    scope: "fr",
    readable: true,
  },

  // — Achats et appels d'offres ——————————————————————————————————
  {
    id: "public-tenders",
    label: "Appels d’offres et marchés publics",
    group: "procurement",
    yields:
      "Le signal le plus explicite qui existe : un besoin décrit, budgété, daté, avec une procédure de réponse officielle et un contact.",
    families: ["direct-demand", "budget", "compliance", "reachability"],
    domains: [
      "boamp.fr",
      "ted.europa.eu",
      "marches-publics.gouv.fr",
      "achatpublic.com",
      "francemarches.com",
      "klekoon.com",
      "e-marchespublics.com",
    ],
    patterns: [
      '(site:boamp.fr OR site:ted.europa.eu OR site:marches-publics.gouv.fr) {topic} {year}',
      '(site:boamp.fr OR site:ted.europa.eu) "{org}"',
      '("appel d\'offres" OR "consultation" OR "cahier des charges" OR "avis de marché") {topic} {year}',
    ],
    scope: "fr",
    readable: true,
  },

  // — Registres et données d'entreprise ————————————————————————————
  {
    id: "company-registries",
    label: "Registres légaux et données ouvertes",
    group: "registries",
    yields:
      "Existence, taille, dirigeants, établissements, évolutions statutaires et comptes déposés — de quoi qualifier et dater sans hypothèse.",
    families: ["growth", "organization-change", "budget"],
    domains: [
      "annuaire-entreprises.data.gouv.fr",
      "pappers.fr",
      "bodacc.fr",
      "data.inpi.fr",
      "societe.com",
      "infogreffe.fr",
      "data.gouv.fr",
    ],
    patterns: [
      '(site:annuaire-entreprises.data.gouv.fr OR site:pappers.fr OR site:societe.com) "{org}"',
      '(site:bodacc.fr OR site:data.inpi.fr) "{org}"',
    ],
    scope: "fr",
    readable: true,
  },

  // — Presse, communiqués et financement ————————————————————————————
  {
    id: "press-releases",
    label: "Communiqués, presse économique et spécialisée",
    group: "press",
    yields:
      "Levées de fonds, acquisitions, nominations, ouvertures, résultats et plans de transformation — datés et citables.",
    families: ["budget", "growth", "organization-change", "cost-pressure", "product-change"],
    domains: [
      "businesswire.com",
      "prnewswire.com",
      "actusnews.com",
      "zonebourse.com",
      "lesechos.fr",
      "usine-nouvelle.com",
      "latribune.fr",
      "maddyness.com",
    ],
    patterns: [
      '"{org}" (communiqué OR "levée de fonds" OR acquisition OR nomination OR résultats) {year}',
      '{topic} ("levée de fonds" OR "plan de transformation" OR investissement) {year}',
      'site:{domain} (presse OR communiqués OR newsroom OR actualités)',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "funding-programs",
    label: "Subventions, aides et financements publics",
    group: "press",
    yields:
      "Un budget fléché et une échéance de mise en œuvre : lauréats d’appels à projets, aides obtenues, programmes de modernisation.",
    families: ["budget", "compliance", "product-change"],
    domains: ["bpifrance.fr", "aides-territoires.beta.gouv.fr", "les-aides.fr", "ademe.fr", "europa.eu"],
    patterns: [
      '(site:bpifrance.fr OR site:aides-territoires.beta.gouv.fr OR site:ademe.fr) {topic} (lauréats OR bénéficiaires) {year}',
      '"{org}" (subvention OR "appel à projets" OR lauréat OR financement) {year}',
    ],
    scope: "fr",
    readable: true,
  },

  // — Réglementation et conformité ————————————————————————————————
  {
    id: "regulation",
    label: "Textes, régulateurs et échéances de conformité",
    group: "regulation",
    yields:
      "Une contrainte externe datée qui rend un chantier obligatoire : la meilleure raison d’ouvrir une conversation aujourd’hui plutôt que dans six mois.",
    families: ["compliance", "security", "budget"],
    domains: ["legifrance.gouv.fr", "eur-lex.europa.eu", "cnil.fr", "anssi.gouv.fr", "journal-officiel.gouv.fr"],
    patterns: [
      '(site:legifrance.gouv.fr OR site:eur-lex.europa.eu OR site:cnil.fr OR site:anssi.gouv.fr) {topic} {year}',
      '{topic} (obligation OR échéance OR "entrée en vigueur" OR mise en conformité) {year}',
    ],
    scope: "eu",
    readable: true,
  },

  // — Voix du client et communautés ————————————————————————————————
  {
    id: "reviews",
    label: "Avis, notations et places de marché",
    group: "voice",
    yields:
      "Les utilisateurs décrivent eux-mêmes la lacune, le contournement ou la raison d’un changement de fournisseur.",
    families: ["customer-voice", "vendor-change", "operational-friction"],
    domains: ["trustpilot.com", "g2.com", "capterra.fr", "glassdoor.fr", "avis-verifies.com"],
    patterns: [
      '(site:trustpilot.com OR site:g2.com OR site:capterra.fr) {topic}',
      '"{org}" (avis OR review OR témoignage OR "étude de cas")',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "communities",
    label: "Communautés et forums professionnels",
    group: "voice",
    yields:
      "Des questions écrites au présent : « comment gérez-vous… », « on fait ça sous Excel », « on cherche une alternative à… ».",
    families: ["direct-demand", "manual-operations", "vendor-change", "customer-voice"],
    domains: ["reddit.com", "stackoverflow.com", "news.ycombinator.com", "discourse.org"],
    patterns: [
      'site:reddit.com {topic} (alternative OR "how do you" OR "comment gérez-vous" OR recommandation)',
      '{topic} forum ("nous utilisons Excel" OR "processus manuel" OR "on cherche une alternative")',
    ],
    scope: "global",
    readable: true,
  },

  // — Plateformes techniques et statut ————————————————————————————
  {
    id: "developer-platforms",
    label: "GitHub, documentation et pages de statut",
    group: "technical",
    yields:
      "Stack réellement observée, intégrations recherchées, tickets publics, incidents et changelogs — des faits vérifiables, pas des inférences.",
    families: ["technology", "security", "vendor-change", "customer-voice"],
    domains: ["github.com", "gitlab.com", "statuspage.io", "status.io"],
    patterns: [
      '(site:github.com OR site:gitlab.com) "{org}"',
      'site:{domain} (documentation OR intégrations OR api OR changelog OR statut)',
      '"{org}" (incident OR panne OR "post-mortem" OR maintenance) {year}',
    ],
    scope: "global",
    readable: true,
  },

  // — Prises de parole et contenus longs ————————————————————————————
  {
    id: "talks-podcasts",
    label: "Interviews, podcasts et rediffusions",
    group: "talks",
    yields:
      "Une heure de parole d’un décideur : priorités, irritants, calendrier et vocabulaire exact — la matière première d’un message personnalisé.",
    families: ["events", "reachability", "organization-change", "operational-friction"],
    domains: ["youtube.com", "podcasts.apple.com", "ausha.co", "spotify.com"],
    patterns: [
      'site:youtube.com "{org}" (interview OR conférence OR témoignage OR webinaire)',
      '"{org}" {role} (podcast OR interview OR "table ronde") {year}',
    ],
    scope: "global",
    readable: true,
  },
  {
    id: "newsroom-rss",
    label: "Newsroom et flux RSS de l’organisation",
    group: "talks",
    yields:
      "La parole officielle à la source, datée, sans intermédiaire : blog, actualités, notes de version, rapports annuels.",
    families: ["product-change", "growth", "compliance", "budget"],
    domains: [],
    patterns: [
      'site:{domain} (actualités OR blog OR presse OR newsroom OR "rapport annuel")',
      'site:{domain} (rss OR feed OR atom)',
    ],
    scope: "global",
    readable: true,
  },
] as const;

const channelsById = new Map(sourceChannels.map((channel) => [channel.id, channel]));

export function getSourceChannel(id: string): SourceChannel | null {
  return channelsById.get(id) ?? null;
}

export function channelsForFamily(familyId: string): SourceChannel[] {
  return sourceChannels.filter((channel) => channel.families.includes(familyId));
}

/**
 * Mots vides écartés de l'extraction : le vocabulaire du problème doit rester
 * celui du marché, pas celui de la grammaire.
 */
const STOPWORDS = new Set([
  "avec", "sans", "pour", "dans", "leur", "leurs", "elle", "elles", "cette", "cettes", "celui",
  "ceux", "plus", "moins", "très", "tres", "être", "etre", "avoir", "faire", "fait", "font",
  "sont", "était", "etait", "chez", "afin", "donc", "mais", "aussi", "entre", "vers", "tout",
  "tous", "toute", "toutes", "autre", "autres", "notre", "nos", "vos", "votre", "quand",
  "comme", "déjà", "deja", "encore", "ainsi", "dont", "qui", "que", "quoi", "sur", "par",
  "des", "les", "une", "aux", "est", "ont", "son", "ses", "lui", "the", "and", "for", "with",
  "that", "this", "from", "their", "have", "has", "are", "was", "were", "them", "they",
  "entreprise", "entreprises", "société", "societe", "clients", "client", "solution",
  "solutions", "service", "services", "offre", "offres", "produit", "produits", "permet",
  "permettre", "grâce", "grace", "notamment", "environ", "plusieurs",
]);

/**
 * Extrait le vocabulaire significatif d'un texte d'offre (résumé, segment
 * prioritaire, événement favorable) pour construire des requêtes qui parlent du
 * problème résolu plutôt que d'un signal générique. L'ordre d'apparition est
 * conservé : les premiers mots d'un résumé portent l'essentiel du sens.
 */
export function extractOfferTerms(sources: readonly (string | null | undefined)[], max = 8): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const source of sources) {
    if (!source) continue;
    for (const raw of source.split(/[^\p{L}\p{N}-]+/u)) {
      const word = raw.trim();
      if (word.length < 4 || word.length > 30) continue;
      const key = word.toLowerCase();
      if (STOPWORDS.has(key) || seen.has(key)) continue;
      if (/^\d+$/.test(key)) continue;
      seen.add(key);
      terms.push(word);
      if (terms.length >= max) return terms;
    }
  }
  return terms;
}

/** Canaux couvrant une liste de familles de signaux, sans doublon. */
export function channelsForFamilies(familyIds: readonly string[]): SourceChannel[] {
  const wanted = new Set(familyIds);
  return sourceChannels.filter((channel) => channel.families.some((family) => wanted.has(family)));
}

export type SourceQueryVars = {
  org?: string | null;
  topic?: string | null;
  role?: string | null;
  domain?: string | null;
  year?: string | number | null;
};

const VARIABLE_PATTERN = /\{(org|topic|role|domain|year)\}/g;

function variableValue(name: string, vars: SourceQueryVars): string | null {
  const raw =
    name === "year"
      ? (vars.year ?? new Date().getFullYear())
      : (vars[name as keyof SourceQueryVars] as string | null | undefined);
  if (raw === null || raw === undefined) return null;
  const value = String(raw).replace(/["\r\n]+/g, " ").replace(/\s+/g, " ").trim();
  return value ? value : null;
}

/**
 * Remplit les gabarits d'un canal. Un gabarit dont une variable est absente
 * est écarté : mieux vaut moins de requêtes que des requêtes trouées.
 */
export function buildChannelQueries(channel: SourceChannel, vars: SourceQueryVars): string[] {
  return channel.patterns.flatMap((pattern) => {
    let missing = false;
    const query = pattern.replace(VARIABLE_PATTERN, (_match, name: string) => {
      const value = variableValue(name, vars);
      if (value === null) {
        missing = true;
        return "";
      }
      return value;
    });
    if (missing) return [];
    const cleaned = query.replace(/\s+/g, " ").trim();
    return cleaned ? [cleaned] : [];
  });
}

/** Requêtes de plusieurs canaux, dédoublonnées, dans l'ordre du catalogue. */
export function buildQueriesForChannels(channelIds: readonly string[], vars: SourceQueryVars): string[] {
  const seen = new Set<string>();
  return channelIds.flatMap((id) => {
    const channel = getSourceChannel(id);
    if (!channel) return [];
    return buildChannelQueries(channel, vars).filter((query) => {
      if (seen.has(query)) return false;
      seen.add(query);
      return true;
    });
  });
}

/** Restitution compacte du catalogue pour le prompt de l'agent. */
export function renderSourceCatalog(): string {
  return (Object.keys(SOURCE_GROUP_LABELS) as SourceGroupId[])
    .map((group) => {
      const channels = sourceChannels.filter((channel) => channel.group === group);
      if (channels.length === 0) return "";
      const lines = channels
        .map((channel) => {
          const domains = channel.domains.length
            ? ` Plateformes : ${channel.domains.join(", ")}.`
            : "";
          const access = channel.readable ? "" : " Résultats de recherche uniquement (pages complètes derrière un compte).";
          return `  - ${channel.label} [${channel.id}] : ${channel.yields}${domains} Familles : ${channel.families.join(", ")}.${access}`;
        })
        .join("\n");
      return `${SOURCE_GROUP_LABELS[group]}\n${lines}`;
    })
    .filter(Boolean)
    .join("\n\n");
}
